/**
 * Privacy Policy — German content (legally binding).
 *
 * IMPORTANT: If you change anything here, update DatenschutzContentEn.tsx
 * accordingly. The German version prevails in case of conflicts.
 *
 * Last updated: 2026-05-24 | Version: 2.4
 */

export function DatenschutzContentDe() {
  return (
    <div className="space-y-12">
      {/* Geltungsbereich (top-level disclosure per DSGVO Art. 13/14) */}
      <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-emerald-900 mb-3">
          Geltungsbereich dieser Datenschutzerklärung
        </h2>
        <p className="text-slate-700 leading-relaxed">
          Diese Datenschutzerklärung gilt für <strong>alle Equna-Dienste</strong>:
        </p>
        <ul className="list-disc list-inside text-slate-700 leading-relaxed mt-2 space-y-1">
          <li>Unsere Website <strong>equna.eu</strong> und alle Unterseiten</li>
          <li>Die Equna Web-App unter <strong>app.equna.eu</strong></li>
          <li>Die Equna Mobile App für <strong>Android</strong> (Google Play Store) und <strong>iOS</strong> (Apple App Store)</li>
        </ul>
        <p className="text-slate-700 leading-relaxed mt-3">
          Wo in dieser Erklärung „Equna", „die App" oder „der Dienst" steht, sind alle drei Plattformen gemeint. Wenn bestimmte Abschnitte nur für Website oder App gelten, ist dies jeweils explizit angegeben.
        </p>
      </section>

      {/* Section 1: Überblick */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          1. Datenschutz auf einen Blick
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Allgemeine Hinweise</h3>
            <p className="text-slate-600 leading-relaxed">
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie unsere Website besuchen oder die Equna App nutzen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Datenerfassung bei Equna</h3>
            <div className="space-y-4">
              <p className="text-slate-600 leading-relaxed">
                <strong className="block text-slate-800 mb-1">Wer ist verantwortlich für die Datenerfassung?</strong>
                Die Datenverarbeitung erfolgt durch den Websitebetreiber. Dessen Kontaktdaten finden Sie in Abschnitt 3.3 sowie im Impressum.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong className="block text-slate-800 mb-1">Wie erfassen wir Ihre Daten?</strong>
                Personenbezogene Daten erheben wir nur, wenn Sie uns diese freiwillig mitteilen (z. B. Registrierung, Ausgabeneintrag, E-Mail) oder wenn technisch notwendige Daten beim Seitenaufruf automatisch anfallen (Server-Logs).
              </p>
              <p className="text-slate-600 leading-relaxed">
                Technisch notwendige Daten (z. B. IP-Adresse, Browsertyp, Betriebssystem, Uhrzeit des Seitenaufrufs) werden automatisch beim Besuch der Website durch unseren Hosting-Anbieter in sogenannten Server-Log-Dateien erfasst. Dies dient der sicheren und stabilen Bereitstellung des Dienstes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Hosting */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          2. Hosting
        </h2>
        <div className="space-y-6">
          <div className="bg-slate-50 p-6 rounded-lg border border-slate-100">
            <p className="text-slate-800 mb-2">
              Unsere Website und App werden bei der <strong>IONOS SE</strong> gehostet.
            </p>
            <address className="not-italic text-slate-600">
              Elgendorfer Str. 57<br />
              56410 Montabaur<br />
              Deutschland
            </address>
          </div>
          <p className="text-slate-600 leading-relaxed">
            Beim Besuch unserer Website erfasst IONOS verschiedene Logfiles inklusive Ihrer IP-Adresse. Die Nutzung von IONOS erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen Darstellung unserer Website.
          </p>
          <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r">
            <strong>Auftragsverarbeitung:</strong><br />
            Wir haben mit IONOS einen Vertrag über Auftragsverarbeitung (AVV) gemäß Art. 28 DSGVO geschlossen.
          </p>
        </div>
      </section>

      {/* Section 3: Allgemeine Hinweise */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          3. Allgemeine Hinweise und Pflichtinformationen
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.1 Datenschutz</h3>
            <p className="text-slate-600 leading-relaxed">
              Wir nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.2 Hinweis zu externen Diensten und Schriftarten</h3>
            <p className="text-slate-600 leading-relaxed">
              Wir binden Schriftarten (Fonts) sowie Bilder lokal von unserem eigenen Server ein. Es wird keine Verbindung zu Servern Dritter (z. B. Google oder anderen externen Anbietern) hergestellt. Eine Weitergabe Ihrer IP-Adresse an solche Anbieter findet nicht statt.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.3 Verantwortliche Stelle</h3>
            <div className="space-y-4">
              <p className="text-slate-600 leading-relaxed">Die verantwortliche Stelle für die Datenverarbeitung ist:</p>
              <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200 inline-block pr-12">
                <strong className="text-slate-900">Patrizio Veglia</strong><br />
                Herbststr. 9<br />
                13409 Berlin<br />
                Deutschland
              </address>
              <p className="text-slate-600">
                E-Mail:{" "}
                <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                  contact@equna.eu
                </a>
              </p>
              <p className="text-slate-500 text-sm italic">
                Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten entscheidet.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">3.4 Datenschutzbeauftragter</h3>
            <p className="text-slate-600 leading-relaxed">
              Ein Datenschutzbeauftragter ist nicht benannt, da die gesetzlichen Voraussetzungen nach Art. 37 Abs. 1 DSGVO nicht vorliegen (keine Pflicht-Kernaktivität mit umfangreicher Verarbeitung besonderer Datenkategorien und keine ständige Beschäftigung von 20 oder mehr Personen mit automatisierter Verarbeitung). Anfragen zum Datenschutz richten Sie bitte direkt an den Verantwortlichen unter <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">contact@equna.eu</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4: Verarbeitung Ihrer Daten und Sicherheit */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          4. Verarbeitung Ihrer Daten und Sicherheit
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.1 Kontodaten bei Registrierung</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Für die Nutzung von Equna ist ein Nutzerkonto erforderlich. Dabei werden folgende Daten verarbeitet:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Daten:</strong> E-Mail-Adresse, selbst gewählter Anzeigename, Passwort (nur als BCrypt-Hash)</li>
              <li><strong>Zweck:</strong> Kontoerstellung, Login, Zuordnung von Ausgaben zu Ihrem Konto, Kommunikation (z. B. Passwort-Reset, Einladungen)</li>
              <li><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
              <li><strong>Speicherdauer:</strong> Bis zur vollständigen Löschung Ihres Kontos; danach ggf. bis zu 30 Tage in rotierenden Backups</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Pflichtbereitstellung:</strong> Die Bereitstellung einer gültigen E-Mail-Adresse und eines Passworts ist zwingende Voraussetzung für den Vertragsabschluss. Ohne diese Daten ist eine Kontoerstellung technisch nicht möglich.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.2 Ausgaben-, Budget- und Gruppendaten</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Der Kern-Zweck von Equna ist das Teilen und Verwalten gemeinsamer Ausgaben innerhalb einer Gruppe (z. B. Paare, Co-Parents):
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Daten:</strong> Ausgabenbeträge, Kategorien, Datum, Beschreibungen (Freitext), Gruppen- und Partner-Zuordnung, Verteilungsschlüssel, Budgets, Monatsabschluss-Bestätigungen</li>
              <li><strong>Zweck:</strong> Aufteilung von Ausgaben, Budgetkontrolle, Monatsabschluss und Ausgleichsberechnung zwischen Gruppenmitgliedern</li>
              <li><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung — Kern-Funktionalität)</li>
              <li><strong>Sichtbarkeit:</strong> Ausschließlich innerhalb Ihrer Gruppe (Sie und Ihr(e) Partner/in)</li>
              <li><strong>Speicherdauer:</strong> Bis zur Kontolöschung oder manuellen Löschung der Ausgaben durch Sie</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Hinweis zu Freitext-Feldern:</strong> Bitte geben Sie in Freitextfeldern (Ausgabenbeschreibung, Kommentare) keine besonders sensiblen Daten über sich oder Dritte ein (z. B. Gesundheitsinformationen, Zahlungsdetails, Inhalte privater Kommunikation). Equna speichert den Freitext unverändert.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.3 Server-Logs und Zugriffsstatistiken</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Bei jedem Aufruf werden technisch notwendige Zugriffsdaten automatisch durch unseren Hosting-Provider (IONOS SE, Deutschland) erfasst:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Daten:</strong> IP-Adresse, Zeitstempel, aufgerufene URL, HTTP-Statuscode, Browser-/Geräte-Kennung (User-Agent), Referrer</li>
              <li><strong>Zweck:</strong> Stabile und sichere Bereitstellung des Dienstes, Fehleranalyse, Missbrauchsabwehr</li>
              <li><strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse am sicheren Betrieb)</li>
              <li><strong>Speicherdauer:</strong> In der Regel 14 Tage, danach automatische Löschung. Bei Hinweis auf Angriffsmuster ggf. bis zu 30 Tage für forensische Analyse.</li>
              <li><strong>Keine Profilbildung:</strong> Es findet keine Verknüpfung der Logs mit Ihrer Nutzeridentität zu Tracking-Zwecken statt.</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.4 Passwort-Sicherheit</h3>
            <p className="text-slate-600 leading-relaxed">
              Ihre Passwörter werden <strong>niemals im Klartext</strong> gespeichert. Wir verwenden den Industriestandard <strong>BCrypt</strong> mit automatischem Salt zur Verschlüsselung.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.5 JWT-Token und Refresh-Tokens</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Zur Authentifizierung verwenden wir <strong>JSON Web Tokens (JWT)</strong> und <strong>Refresh-Tokens</strong>:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>JWT-Token:</strong> Gültig für 60 Minuten, gespeichert im Browser-Speicher</li>
              <li><strong>Refresh-Token:</strong> Gültig für 90 Tage, gespeichert als <strong>HttpOnly-Cookie</strong> (XSS-Schutz)</li>
              <li><strong>Secure-Flag:</strong> Cookies werden nur über HTTPS übertragen</li>
              <li><strong>SameSite-Flag:</strong> Schutz vor CSRF-Angriffen</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (technische Notwendigkeit für Login-Funktion)
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.6 Verschlüsselung</h3>
            <p className="text-slate-600 leading-relaxed">
              Die gesamte Datenübertragung zwischen Ihrem Gerät und unseren Servern erfolgt über <strong>HTTPS (TLS 1.3)</strong>.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">4.7 Rate Limiting und Brute-Force-Schutz</h3>
            <p className="text-slate-600 leading-relaxed">
              Zum Schutz vor unbefugten Zugriffen begrenzen wir die Anzahl der Anmeldeversuche. Nach fünf fehlgeschlagenen Login-Versuchen wird das Konto temporär gesperrt. Zusätzlich werden verdächtige Zugriffsmuster (z. B. viele Anfragen von derselben IP-Adresse in kurzer Zeit) automatisch blockiert.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Kontosicherheit und Missbrauchsschutz)
            </p>
          </div>
        </div>
      </section>

      {/* Section 5: Datenübermittlung an Dritte */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          5. Datenübermittlung an Dritte
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.1 Hosting</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Unsere Website und App werden bei folgendem Anbieter gehostet:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">IONOS SE</strong><br />
              Elgendorfer Str. 57<br />
              56410 Montabaur, Germany
            </address>
            <p className="text-slate-600 leading-relaxed mt-3">
              Der Hosting-Provider verarbeitet folgende Daten in unserem Auftrag: Server-Logs (IP-Adressen, Zugriffszeiten) und Datenbank-Inhalte (verschlüsselt).
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Rechtsgrundlage:</strong> Art. 28 DSGVO (Auftragsverarbeitung)
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Auftragsverarbeitungsvertrag (AVV):</strong> Vorhanden (gemäß Art. 28 Abs. 3 DSGVO)
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.2 Cloud-Speicherung (Receipt-Fotos) &amp; OCR</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Wenn Sie Fotos von Belegen (Receipts) in der App hochladen, werden diese in unserem Cloud-Speicher gespeichert:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">IONOS SE</strong> (S3-kompatibler Object Storage)<br />
              Elgendorfer Str. 57<br />
              56410 Montabaur, Germany
            </address>
            <div className="mt-3 space-y-2">
              <p className="text-slate-600 leading-relaxed">
                <strong>Gespeicherte Daten:</strong> Hochgeladene Belegfotos (JPEG/PNG) und Verknüpfung zur zugehörigen Ausgabe.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Speicherdauer:</strong> Free-Tier: 60 Tage nach Upload. Premium-Tier: 365 Tage nach Upload.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Löschung:</strong> Belegfotos werden automatisch nach Ablauf der Aufbewahrungsfrist gelöscht. Sie können Fotos jederzeit manuell in der App löschen.
              </p>
            </div>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>OCR-Texterkennung auf eigenem Server:</strong> Belegfotos werden optional durch unsere selbst gehostete OCR-Engine (PaddleOCR) verarbeitet, um Beträge und Kategorien vorzuschlagen. Die Bilder verlassen dabei <strong>nie unseren Server in Deutschland</strong> — es werden <strong>keine externen KI-/Cloud-Dienste</strong> wie Google Vision, AWS Textract o.Ä. verwendet.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.3 E-Mail-Versand</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Für den Versand von E-Mails (z. B. Passwort-Reset) nutzen wir:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">IONOS SE</strong> (SMTP-Server)<br />
              Elgendorfer Str. 57<br />
              56410 Montabaur, Germany
            </address>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">
              5.4 Push-Benachrichtigungen (Firebase Cloud Messaging — Privacy-by-Design)
            </h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Für Echtzeit-Benachrichtigungen auf Android/iOS nutzen wir:
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">Google Firebase (Firebase Cloud Messaging / FCM)</strong><br />
              Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland
            </address>
            <div className="mt-3">
              <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mb-3">
                <strong>Privacy-by-Design Ansatz:</strong> An Google wird lediglich ein <strong>technischer Auslöser</strong> (Notification-ID + Event-Typ) übermittelt. <strong>Keine persönlichen Daten</strong> (Namen, Beträge, Kategorien) werden an FCM gesendet. Der tatsächliche Benachrichtigungsinhalt wird direkt und verschlüsselt von unserem Server in Deutschland geladen.
              </p>
              <div className="space-y-2">
                <p className="text-slate-600 leading-relaxed">
                  <strong>Gespeicherte Daten:</strong> FCM-Token (Push-Zustellung), lokal generierte Device-ID (UUID, ausschließlich auf Ihrem Gerät im LocalStorage), Plattform (android/ios), Gerätemodell (optional, zur Fehleranalyse).
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch Push-Aktivierung)
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>Speicherdauer:</strong> Bis zum Logout oder Deinstallation der App (inaktive Tokens nach 90 Tagen gelöscht).
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>Drittlandtransfer:</strong> Vertragspartner ist Google Ireland Limited (EU). Die Konzernmutter Google LLC (USA) kann im Rahmen der Dienstleistung Daten in die USA übertragen. Grundlage: EU-US Data Privacy Framework (Google ist aktiv zertifiziert, <a href="https://www.dataprivacyframework.gov/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">dataprivacyframework.gov</a>) sowie EU-Standardvertragsklauseln (Art. 46 DSGVO) als zusätzliche Absicherung.
                </p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed border-l-4 border-amber-500 pl-4 bg-amber-50/50 py-2 rounded-r mt-3">
              <strong>Aktueller Status:</strong> Push-Benachrichtigungen sind derzeit deaktiviert. Die Einbindung erfolgt erst, sobald die Funktion freigeschaltet wird. Sie werden in der App über die Aktivierung informiert und können der Verarbeitung jederzeit durch Deaktivierung in den System-Einstellungen widersprechen.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.5 System-Teilen-Funktion (Invite-Links)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Wenn Sie in der App eine Einladung an Ihren Partner verschicken, nutzen wir die <strong>systemeigene Teilen-Funktion</strong> von Android bzw. iOS. Dabei übergibt die App den Einladungs-Link an das Betriebssystem, das Ihnen ein Auswahlmenü für Messenger, E-Mail-Apps o.Ä. anzeigt.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Übermittelte Daten:</strong> Der Invite-Link (enthält einen zufälligen Einladungscode, keine Namen oder E-Mail-Adressen)</li>
              <li><strong>Empfänger:</strong> Die von Ihnen gewählte App (z. B. WhatsApp, Signal, E-Mail) — dieser Drittanbieter ist <strong>nicht von Equna</strong> kontrolliert</li>
              <li><strong>Unsere Rolle:</strong> Wir erhalten keine Information darüber, an welche App oder an welche Person Sie den Link weitergeben</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung durch bewusste Auslösung der Teilen-Funktion)
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Wichtig:</strong> Für die von Ihnen gewählte Drittanbieter-App (WhatsApp, E-Mail-Provider etc.) gelten deren eigene Datenschutzbestimmungen. Equna hat darauf keinen Einfluss.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.6 Have I Been Pwned (Passwort-Sicherheitscheck)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Bei der Registrierung und bei Passwortänderungen prüfen wir, ob das gewählte Passwort in bekannten Datenlecks aufgetaucht ist. Dazu nutzen wir den Dienst <strong>Have I Been Pwned</strong> (betrieben von Troy Hunt, Australien/USA).
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Übermittelte Daten:</strong> Nur die ersten 5 Zeichen eines SHA-1 Hash des Passworts (k-Anonymität)</li>
              <li><strong>Ihr Passwort wird NIEMALS übertragen</strong> — weder im Klartext noch als vollständiger Hash</li>
              <li><strong>Keine Speicherung</strong> der übermittelten Hash-Präfixe bei uns oder bei Have I Been Pwned</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Kontosicherheit und Schutz vor unbefugtem Zugriff)
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              ✓ Datenschutzfreundlich durch k-Anonymität (Hash-Präfix-Verfahren)
            </p>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Drittlandtransfer:</strong> Der übermittelte 5-Zeichen-Hash-Präfix stellt nach herrschender Auffassung kein personenbezogenes Datum im Sinne des Art. 4 Nr. 1 DSGVO dar, da ihm etwa 500 mögliche Passwörter zugeordnet werden können (k-Anonymität) und keine Identifizierung möglich ist. Ein Drittlandtransfer im Sinne des Art. 44 DSGVO findet somit nicht statt.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.7 Reaktionen und Kommentare in Gruppen</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Innerhalb einer Gruppe können Nutzer zu Ausgaben <strong>Emoji-Reaktionen</strong> und <strong>Kommentare</strong> hinterlassen. Diese werden auf unseren Servern gespeichert und sind für alle Mitglieder der jeweiligen Gruppe sichtbar.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Gespeicherte Daten:</strong> Emoji/Reaktion bzw. Kommentar-Text, Ersteller-ID, Zeitstempel, Zuordnung zur Ausgabe</li>
              <li><strong>Sichtbarkeit:</strong> Nur innerhalb der Gruppe — niemals öffentlich, nie an Dritte</li>
              <li><strong>Löschung:</strong> Mit der zugehörigen Ausgabe oder manuell durch den Ersteller</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung — Kommunikation zwischen Gruppenmitgliedern ist Kernbestandteil des Dienstes)
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.8 Cloudflare (Content Delivery Network für die Website)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Die Auslieferung statischer Inhalte unserer Website <strong>equna.eu</strong> erfolgt über das im IONOS-Webhosting-Paket integrierte CDN-Netzwerk der Cloudflare, Inc.
            </p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">Cloudflare, Inc.</strong><br />
              101 Townsend St., San Francisco, CA 94107, USA
            </address>
            <div className="mt-3 space-y-2">
              <p className="text-slate-600 leading-relaxed">
                <strong>Verarbeitete Daten:</strong> IP-Adresse, Anfragemetadaten (URL, Zeitstempel, User-Agent), ausgelieferte statische Assets (CSS, Bilder, Schriften). Für deutsche Besucher wird bevorzugt der Cloudflare-Edge-Server in Frankfurt genutzt.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Zweck:</strong> Beschleunigte Auslieferung der Website, Schutz vor DDoS-Angriffen, technische Stabilität.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer schnellen und stabilen Website)
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Drittlandtransfer:</strong> Cloudflare hat seinen Hauptsitz in den USA. Die Verarbeitung deutscher Besucher erfolgt regelmäßig auf dem EU-Edge-Server Frankfurt; eine Übermittlung in die USA kann jedoch nicht vollständig ausgeschlossen werden. Rechtsgrundlage: EU-US Data Privacy Framework (Cloudflare ist aktiv zertifiziert) sowie EU-Standardvertragsklauseln (Art. 46 DSGVO).
              </p>
              <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r">
                <strong>Wichtig:</strong> Cloudflare wird ausschließlich für die statische Landing Page (equna.eu) verwendet — <strong>nicht</strong> für die eigentliche Anwendung (app.equna.eu) und <strong>nicht</strong> für die API. Ihre Inhaltsdaten (Ausgaben, Budgets, Belege) laufen <strong>nie über Cloudflare</strong>.
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.9 Zusammenfassung: Drittlandtransfer</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Ihre Inhaltsdaten (Ausgaben, Budgets, Belege, Kontoinformationen) werden ausschließlich auf deutschen Servern in Frankfurt verarbeitet. In folgenden Ausnahmefällen findet ein Transfer in Drittländer statt:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-2">
              <li>
                <strong>Cloudflare (USA)</strong> — CDN für statische Inhalte der Landing Page. Grundlage: EU-US DPF + SCCs. Keine Inhaltsdaten.
              </li>
              <li>
                <strong>Firebase/Google LLC (USA)</strong> — <em>künftig</em> bei aktivierten Push-Benachrichtigungen. Nur technische Trigger, keine Inhalte. Grundlage: EU-US DPF + SCCs.
              </li>
              <li>
                <strong>Have I Been Pwned (Cloudflare-Infrastruktur, Anbieter in Australien)</strong> — Passwort-Sicherheitscheck über Hash-Präfix. Durch k-Anonymität kein personenbezogener Transfer im Sinne Art. 44 DSGVO.
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              <strong>Alle Ihre Inhaltsdaten</strong> — sämtliche Ausgaben, Budgets, Belege, Kontoinformationen, Gruppen- und Kommentardaten — werden <strong>ausschließlich auf deutschen IONOS-Servern</strong> gespeichert und verarbeitet. Diese Daten verlassen Deutschland nicht.
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">5.10 Keine Weitergabe an Dritte ohne Einwilligung</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Wir geben Ihre personenbezogenen Daten <strong>nicht</strong> an Dritte weiter, außer:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li>Sie haben ausdrücklich eingewilligt (Art. 6 Abs. 1 lit. a DSGVO)</li>
              <li>Es besteht eine gesetzliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO)</li>
              <li>Es ist zur Vertragserfüllung erforderlich (Art. 6 Abs. 1 lit. b DSGVO)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 6: Cookies und lokale Speicherung */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          6. Cookies und lokale Speicherung
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">6.1 Was sind Cookies?</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden. Wir verwenden <strong>ausschließlich technisch notwendige Cookies und lokale Speicher</strong>. Alle dienen dem Betrieb von Website und App und erfordern keine Einwilligung:
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>RefreshToken-Cookie:</strong> Login-Funktion (90 Tage Gültigkeit)</li>
              <li><strong>Session-Cookie:</strong> Sitzungsverwaltung (bis Browser-Schließung)</li>
              <li><strong>Präferenz-Speicherung:</strong> Ihre UI-Einstellungen (Theme, Sprache) im lokalen Speicher Ihres Browsers</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Rechtsgrundlagen:</strong> § 25 Abs. 2 Nr. 2 TDDDG (der Zugriff auf Ihr Endgerät ist unbedingt erforderlich, um den gewünschten Dienst bereitzustellen) sowie Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO für die anschließende Verarbeitung der Daten (Vertragserfüllung / berechtigtes Interesse am sicheren Betrieb).
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              <strong>Hinweis:</strong> Wir verwenden <strong>keine Analytics-, Tracking- oder Werbe-Cookies</strong>. Ein Cookie-Consent-Banner wird nicht eingesetzt, da keine einwilligungspflichtigen Dienste zum Einsatz kommen.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">6.2 Lokale Speicherung (LocalStorage, IndexedDB)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Wir speichern folgende Daten <strong>lokal auf Ihrem Gerät</strong> (kein Server-Zugriff):
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Persönliche Ausgaben:</strong> Nur auf Ihrem Gerät gespeichert — im Browser via IndexedDB/LocalStorage, auf Mobilgeräten in einer verschlüsselten SQLite-Datenbank (<strong>SQLCipher mit AES-256</strong>)</li>
              <li><strong>Persönliche Budgets:</strong> Nur auf Ihrem Gerät gespeichert (gleiche Verschlüsselung)</li>
              <li><strong>UI-Präferenzen:</strong> Theme, Sprache, Filter-Einstellungen</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              Diese Daten verlassen <strong>niemals Ihr Gerät</strong> und werden nicht an unsere Server übertragen. Der Verschlüsselungs-Schlüssel wird sicher im Keychain (iOS) bzw. EncryptedSharedPreferences (Android) abgelegt.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong>OS-Backups (iCloud / Google Backup):</strong> Wenn Sie auf Ihrem Mobilgerät die systemeigene Backup-Funktion aktiviert haben, werden App-Daten — wie bei anderen Apps auch — je nach OS-Einstellung in das jeweilige Cloud-Backup einbezogen (Apple iCloud bzw. Google One / Google Drive). Diese Backups unterliegen der Datenschutz-Verantwortung von Apple bzw. Google. Unsere verschlüsselte Mobile-Datenbank (SQLCipher mit AES-256) bleibt auch im Backup verschlüsselt. Sie können dieses Verhalten jederzeit in den System-Einstellungen Ihres Geräts steuern.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">6.3 App-Berechtigungen (Android &amp; iOS)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Die Equna-App fordert folgende Berechtigungen an. Alle Berechtigungen sind <strong>optional</strong> und werden erst zur Laufzeit abgefragt — sie werden nie ungefragt im Hintergrund erteilt.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1">
              <li><strong>Kamera:</strong> Fotografieren von Kassenbelegen direkt in der App (optional, nur wenn Sie einen Beleg hinzufügen)</li>
              <li><strong>Fotobibliothek (lesen):</strong> Auswählen bereits vorhandener Fotos als Belege</li>
              <li><strong>Fotobibliothek (schreiben, iOS):</strong> Speichern eines neu aufgenommenen Belegfotos in Ihrer Galerie</li>
              <li><strong>Push-Benachrichtigungen:</strong> Empfang von Echtzeit-Benachrichtigungen (neue Ausgabe im Partner-Konto, Budget-Warnung, Monatsabschluss)</li>
              <li><strong>Internet:</strong> Verbindung zum Equna-Server (technische Voraussetzung, keine Laufzeit-Abfrage)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-4">
              <strong>Nicht angefordert werden:</strong> Standort, Kontakte, Mikrofon, Kalender, Telefon, SMS, Bluetooth, Bewegungsdaten, biometrische Daten.
            </p>
            <p className="text-slate-600 leading-relaxed mt-3">
              <strong>Widerruf:</strong> Alle Berechtigungen können jederzeit in den System-Einstellungen (Android: Einstellungen → Apps → Equna; iOS: Einstellungen → Equna) widerrufen werden. Die App funktioniert auch ohne Kamera- oder Push-Berechtigung (mit eingeschränktem Funktionsumfang).
            </p>
          </div>
        </div>
      </section>

      {/* Section 7: Ihre Rechte */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          7. Ihre Rechte als betroffene Person
        </h2>
        <div className="space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.1 Auskunftsrecht (Art. 15 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">
              Sie haben das Recht, jederzeit Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In der App:</strong> Profil → „Datenauskunft &amp; Export (DSGVO-konform)". Alle Nutzer — Free wie Premium — können ihre vollständigen Daten jederzeit als JSON-Datei exportieren. Premium-Nutzer haben zusätzlich eine Inline-Anzeige im Profil als Komfort-Feature. Der inhaltliche Umfang der nach Art. 15 DSGVO bereitgestellten Daten ist in beiden Tarifen identisch.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.2 Recht auf Berichtigung (Art. 16 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">Sie können Ihre Daten jederzeit berichtigen.</p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In der App:</strong> Profil → „Persönliche Informationen" → Daten bearbeiten
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.3 Recht auf Löschung (Art. 17 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">Sie haben das Recht, die Löschung Ihrer personenbezogenen Daten zu verlangen.</p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In der App:</strong> Profil → „Konto löschen" → Sicherheitsbestätigung → Konto wird vollständig gelöscht
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-2 rounded-r mt-3">
              <strong>Hinweis zu Backups:</strong> Nach der Kontolöschung können einzelne Daten für einen kurzen Zeitraum (max. 30 Tage) in rotierenden Backups vorhanden sein, bis diese überschrieben werden. Eine Wiederherstellung nach der Löschung ist ausgeschlossen.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.4 Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">
              Sie haben das Recht, Ihre Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten. Dieses Recht steht <strong>allen Nutzern — Free wie Premium — in vollem Umfang</strong> zur Verfügung.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>In der App:</strong> Profil → „Datenauskunft &amp; Export" → „Als JSON exportieren"
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Format:</strong> JSON (maschinenlesbar, Import in andere Dienste möglich)
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.5 Widerspruchsrecht (Art. 21 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">
              Sie haben das Recht, der Verarbeitung Ihrer personenbezogenen Daten zu widersprechen.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Kontakt:</strong>{" "}
              <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                contact@equna.eu
              </a>
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.6 Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">
              Sie können die Einschränkung der Verarbeitung Ihrer Daten verlangen.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Kontakt:</strong>{" "}
              <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                contact@equna.eu
              </a>
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.7 Widerrufsrecht bei Einwilligung</h3>
            <p className="text-slate-600 leading-relaxed">
              Falls Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese jederzeit widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1 mt-2">
              <li><strong>Push-Benachrichtigungen:</strong> Direkt in den System-Einstellungen deaktivieren (Android: Einstellungen → Apps → Equna; iOS: Einstellungen → Equna).</li>
              <li><strong>Sonstige Einwilligungen:</strong> Per E-Mail an <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">contact@equna.eu</a>.</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">7.8 Keine automatisierte Entscheidungsfindung (Art. 22 DSGVO)</h3>
            <p className="text-slate-600 leading-relaxed">
              Es findet <strong>keine automatisierte Entscheidungsfindung</strong> im Sinne des Art. 22 DSGVO statt. Insbesondere werden <strong>keine Profile</strong> erstellt, die rechtliche Wirkung entfalten oder Sie in ähnlicher Weise erheblich beeinträchtigen.
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              Alle Entscheidungen (z. B. Kategorie-Vorschläge aus OCR-Belegerkennung) sind reine Vorschläge und werden erst wirksam, wenn Sie sie manuell bestätigen.
            </p>
          </div>
        </div>
      </section>

      {/* Section 8: Minderjährige */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          8. Minderjährige und Daten über Kinder
        </h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">8.1 Altersbeschränkung</h3>
            <p className="text-slate-600 leading-relaxed">
              Unsere App richtet sich <strong>nicht an Personen unter 16 Jahren</strong>. Sollten Sie feststellen, dass ein Minderjähriger ohne Einwilligung der Erziehungsberechtigten ein Konto erstellt hat, kontaktieren Sie uns bitte umgehend.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">8.2 Daten über Kinder (Co-Parenting-Kontext)</h3>
            <p className="text-slate-600 leading-relaxed mb-3">
              Equna richtet sich an Erwachsene zur Verwaltung gemeinsamer Ausgaben — auch im Co-Parenting-Kontext. Dabei können Nutzer Ausgaben dokumentieren, die ihre Kinder betreffen.
            </p>
            <p className="text-slate-600 leading-relaxed border-l-4 border-emerald-500 pl-4 bg-emerald-50/50 py-3 rounded-r mb-3">
              <strong>Privacy by Design (Art. 25 DSGVO):</strong> Die App sieht die strukturierte Erfassung sensibler Kinderdaten <strong>nicht vor</strong>. Es existieren keine Eingabefelder für Geburtsdatum, Gesundheitsdaten, Adressen oder andere sensible Informationen zu Kindern.
            </p>
            <ul className="list-disc list-inside text-slate-600 leading-relaxed space-y-1 mb-3">
              <li><strong>Free-Tier:</strong> Ausgaben können der generischen Kategorie „Kind" zugeordnet werden — keine Namen oder sonstigen Kinderdaten werden strukturiert gespeichert.</li>
              <li><strong>Premium-Tier:</strong> Nutzer können optional Kind-Profile mit <strong>Vornamen</strong> anlegen, um Ausgaben zuzuordnen. Weitere Felder (Alter, Geburtsdatum, Gesundheit o. Ä.) sind technisch nicht vorgesehen.</li>
              <li><strong>Freitext-Felder</strong> (z. B. Beschreibung einer Ausgabe): Der Nutzer ist selbst dafür verantwortlich, welche Informationen er eingibt. Wir raten ausdrücklich davon ab, sensible Daten über Kinder (z. B. Gesundheitszustand, Schulprobleme) in Freitextfelder einzugeben.</li>
            </ul>
            <p className="text-slate-600 leading-relaxed">
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse des Elternteils zur Dokumentation kindbezogener Ausgaben, insbesondere für Co-Parenting-Abrechnungen).
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong>Speicherdauer:</strong> Wie die zugehörige Ausgabe oder bis zur Kontolöschung. Kind-Profile (Premium) können jederzeit manuell gelöscht werden.
            </p>
          </div>
        </div>
      </section>

      {/* Section 9: Beschwerderecht */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          9. Beschwerderecht bei Aufsichtsbehörde
        </h2>
        <div className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung Ihrer personenbezogenen Daten zu beschweren.
          </p>
          <div>
            <p className="text-slate-800 font-medium mb-2">Zuständige Aufsichtsbehörde:</p>
            <address className="not-italic text-slate-700 bg-slate-50 p-4 rounded border border-slate-200">
              <strong className="text-slate-900">Berliner Beauftragte für Datenschutz und Informationsfreiheit</strong><br />
              Alt-Moabit 59-61<br />
              10555 Berlin<br />
              <span className="text-slate-500 text-sm">(Eingang: Alt-Moabit 60)</span><br />
              Telefon: +49 30 13889-0<br />
              Fax: +49 30 2155050<br />
              E-Mail: <a href="mailto:mailbox@datenschutz-berlin.de" className="text-emerald-600 hover:text-emerald-700 font-medium">mailbox@datenschutz-berlin.de</a><br />
              <a href="https://www.datenschutz-berlin.de/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">
                www.datenschutz-berlin.de
              </a>
            </address>
          </div>
          <p className="text-slate-600 leading-relaxed">
            <strong>Liste aller Aufsichtsbehörden:</strong>{" "}
            <a href="https://www.datenschutzkonferenz-online.de/" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">
              www.datenschutzkonferenz-online.de
            </a>
          </p>
        </div>
      </section>

      {/* Section 10: Änderungen */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          10. Änderungen dieser Datenschutzerklärung
        </h2>
        <div className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            Wir behalten uns vor, diese Datenschutzerklärung zu aktualisieren, um sie an geänderte Rechtslagen oder Änderungen unserer Dienste anzupassen.
          </p>
          <p className="text-slate-600 leading-relaxed">
            <strong>Benachrichtigung:</strong> Bei wesentlichen Änderungen werden Sie per E-Mail oder In-App-Benachrichtigung informiert.
          </p>
          <p className="text-slate-600 leading-relaxed">
            <strong>Empfehlung:</strong> Überprüfen Sie diese Seite regelmäßig auf Aktualisierungen.
          </p>
        </div>
      </section>

      {/* Section 11: Kontakt */}
      <section>
        <h2 className="text-2xl font-bold text-slate-900 mb-6 pb-2 border-b border-slate-100">
          11. Kontakt bei Datenschutzfragen
        </h2>
        <div className="space-y-4">
          <p className="text-slate-600 leading-relaxed">
            Bei Fragen zum Datenschutz können Sie uns jederzeit kontaktieren:
          </p>
          <div className="space-y-2">
            <p className="text-slate-600 leading-relaxed">
              <strong>E-Mail:</strong>{" "}
              <a href="mailto:contact@equna.eu" className="text-emerald-600 hover:text-emerald-700 font-medium">
                contact@equna.eu
              </a>
            </p>
            <p className="text-slate-600 leading-relaxed">
              <strong>Post:</strong> Patrizio Veglia, Herbststr. 9, 13409 Berlin, Germany
            </p>
          </div>
        </div>
      </section>

      {/* Stand / Version */}
      <div className="pt-6 border-t border-slate-200 text-slate-500 text-sm">
        <p><strong>Stand:</strong> 24. Mai 2026</p>
        <p><strong>Version:</strong> 2.4 — Aktualisierte Kontaktdaten der zuständigen Aufsichtsbehörde (BlnBDI: neue Anschrift Alt-Moabit 59-61, 10555 Berlin, plus Telefon/Fax/E-Mail).</p>
        <p className="mt-2"><strong>Version 2.3</strong> (21. April 2026) — Ergänzungen. Transparenzhinweis zum Datenschutzbeauftragten (3.4: nicht benannt, da Voraussetzungen Art. 37 DSGVO nicht erfüllt). Hinweis auf OS-seitige Backup-Funktionen iCloud / Google Backup (6.2) für Mobilgeräte.</p>
        <p className="mt-2"><strong>Version 2.2</strong> (21. April 2026) — Cookies (6.1) unter § 25 TDDDG; Widerrufsrecht (7.7) an tatsächliche Umsetzung angepasst; Betroffenenrechte 7.1/7.4 Premium-unabhängig klargestellt.</p>
        <p className="mt-2"><strong>Version 2.1</strong> (21. April 2026) — Strukturelle Überarbeitung mit eigenständigen Abschnitten zu Kontodaten, Ausgaben/Budgets/Gruppen, Server-Logs, Cloudflare-CDN und Drittlandtransfer-Zusammenfassung.</p>
        <p className="mt-2"><strong>Version 2.0</strong> (21. April 2026) — Kanonische Single-Source-of-Truth-Fassung für Website &amp; App mit SQLCipher, PaddleOCR, System-Teilen und lokaler Device-ID.</p>
      </div>
    </div>
  );
}
